import { Box, Container, Divider, Link, Stack, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";

const LAST_UPDATED = "April 24, 2026";

export default function TermsOfServicePage() {
  const { t } = useTranslation("pages-account");

  return (
    <Container maxWidth="md" sx={{ py: { xs: 5, md: 8 } }}>
      <Stack spacing={1} sx={{ mb: 5 }}>
        <Typography
          variant="overline"
          color="secondary.main"
          sx={{ fontWeight: 500, letterSpacing: "0.08em" }}
        >
          {t("TermsOfServicePage.eyebrow")}
        </Typography>
        <Typography variant="h3" component="h1" sx={{ fontWeight: 500 }}>
          {t("TermsOfServicePage.heading")}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t("TermsOfServicePage.lastUpdated", { date: LAST_UPDATED })}
        </Typography>
      </Stack>

      <Stack spacing={4} sx={{ "& p": { lineHeight: 1.7 } }}>
        <Section>
          <Typography variant="body1">
            <Trans
              i18nKey="TermsOfServicePage.intro"
              t={t}
              components={[
                <strong key="b0" />,
                <strong key="b1" />,
                <strong key="b2" />,
                <strong key="b3" />,
                <strong key="b4" />,
              ]}
            />
          </Typography>
        </Section>

        <Section title={t("TermsOfServicePage.section1.title")}>
          <Typography variant="body1">
            {t("TermsOfServicePage.section1.body")}
          </Typography>
        </Section>

        <Section title={t("TermsOfServicePage.section2.title")}>
          <Typography variant="body1">
            <Trans
              i18nKey="TermsOfServicePage.section2.body"
              t={t}
              components={[<Link key="b0" href="mailto:support@career-forge.org" />]}
            />
          </Typography>
        </Section>

        <Section title={t("TermsOfServicePage.section3.title")}>
          <Typography variant="body1">{t("TermsOfServicePage.section3.intro")}</Typography>
          <Box component="ul" sx={{ pl: 3, "& li": { mb: 1, lineHeight: 1.7 } }}>
            <li>{t("TermsOfServicePage.section3.unlawful")}</li>
            <li>{t("TermsOfServicePage.section3.reverseEngineer")}</li>
            <li>{t("TermsOfServicePage.section3.resell")}</li>
            <li>{t("TermsOfServicePage.section3.interfere")}</li>
            <li>{t("TermsOfServicePage.section3.harassing")}</li>
          </Box>
        </Section>

        <Section title={t("TermsOfServicePage.section4.title")}>
          <Typography variant="body1">
            <Trans
              i18nKey="TermsOfServicePage.section4.billing"
              t={t}
              components={[<Link key="b0" component={RouterLink} to="/pricing" />]}
            />
          </Typography>
          <Typography variant="body1">
            <Trans
              i18nKey="TermsOfServicePage.section4.cancellation"
              t={t}
              components={[<Link key="b0" component={RouterLink} to="/refunds" />]}
            />
          </Typography>
        </Section>

        <Section title={t("TermsOfServicePage.section5.title")}>
          <Typography variant="body1">
            {t("TermsOfServicePage.section5.aiContent")}
          </Typography>
          <Typography variant="body1">
            {t("TermsOfServicePage.section5.aiAccuracy")}
          </Typography>
        </Section>

        <Section title={t("TermsOfServicePage.section6.title")}>
          <Typography variant="body1">
            {t("TermsOfServicePage.section6.body")}
          </Typography>
        </Section>

        <Section title={t("TermsOfServicePage.section7.title")}>
          <Typography variant="body1">
            <Trans
              i18nKey="TermsOfServicePage.section7.userContent"
              t={t}
              components={[<strong key="b0" />]}
            />
          </Typography>
          <Typography variant="body1">
            {t("TermsOfServicePage.section7.represent")}
          </Typography>
        </Section>

        <Section title={t("TermsOfServicePage.section8.title")}>
          <Typography variant="body1">
            {t("TermsOfServicePage.section8.body")}
          </Typography>
        </Section>

        <Section title={t("TermsOfServicePage.section9.title")}>
          <Typography variant="body1">
            {t("TermsOfServicePage.section9.body")}
          </Typography>
        </Section>

        <Section title={t("TermsOfServicePage.section10.title")}>
          <Typography variant="body1">
            {t("TermsOfServicePage.section10.body")}
          </Typography>
        </Section>

        <Section title={t("TermsOfServicePage.section11.title")}>
          <Typography variant="body1">
            <Trans
              i18nKey="TermsOfServicePage.section11.body"
              t={t}
              components={[<Link key="b0" href="mailto:support@career-forge.org" />]}
            />
          </Typography>
        </Section>

        <Section title={t("TermsOfServicePage.section12.title")}>
          <Typography variant="body1">
            {t("TermsOfServicePage.section12.body")}
          </Typography>
        </Section>

        <Section title={t("TermsOfServicePage.section13.title")}>
          <Typography variant="body1">
            {t("TermsOfServicePage.section13.body")}
          </Typography>
        </Section>

        <Section title={t("TermsOfServicePage.section14.title")}>
          <Typography variant="body1">
            <Trans
              i18nKey="TermsOfServicePage.section14.body"
              t={t}
              components={[<Link key="b0" href="mailto:support@career-forge.org" />]}
            />
          </Typography>
        </Section>

        <Divider />

        <Typography variant="caption" color="text.secondary">
          {t("TermsOfServicePage.disclaimer")}
        </Typography>
      </Stack>
    </Container>
  );
}

function Section({ title, children }: { title?: string; children: React.ReactNode }) {
  return (
    <Box component="section">
      {title && (
        <Typography variant="h5" component="h2" sx={{ fontWeight: 500, mb: 2 }}>
          {title}
        </Typography>
      )}
      <Stack spacing={2}>{children}</Stack>
    </Box>
  );
}
